import { ruleDefinitionSchema } from './contracts';
import type { AlertRule, Capability, RuleDefinition, SensorScope } from './contracts';

export const conditionLabels: Record<RuleDefinition['condition']['kind'], string> = {
  no_data: 'Manglende data',
  numeric: 'Måleværdi over eller under en grænse',
  fault: 'Rapporteret fejl',
  message: 'Tekst i en SMS',
  delta: 'Ændring mellem to målinger',
};
export const metricLabels: Record<string, string> = {
  completed_week_hours: 'Registrerede timer i senest afsluttede uge',
  inactive_days: 'Dage uden rapporteret aktivitet',
  level_cm: 'Niveau (cm)',
  temperature_c: 'Temperatur (°C)',
  temperature_high_c: 'Høj temperatur (°C)',
  pm10: 'PM10',
};
export const faultLabels: Record<string, string> = {
  '*': 'Alle rapporterede fejl, hver for sig',
  general_alarm: 'Generel alarm',
  leaking_valves: 'Mulig utæt ventil',
  binder_low: 'Binder mangler',
  water_low: 'Vand mangler',
  binder_fault: 'Binderfejl',
  flow_low: 'For lavt flow',
  flow_high: 'For højt flow',
  emergency_stop: 'Nødstop',
  water_fault: 'Vandfejl',
  motor_thermal_fault: 'Motorværn / termofejl',
};
export const ruleModeLabels = {
  live: 'LIVE · aktiv drift',
  test: 'Test · uden driftsnotifikationer',
  not_in_use: 'Sat på pause',
};

export function commonCapabilities(capabilities: Capability[], types: string[]) {
  const selected = types.map((type) => capabilities.find((item) => item.sensorType === type));
  const valid = selected.length > 0 && selected.every(Boolean);
  const known = selected.filter((item): item is Capability => Boolean(item));
  const metrics = valid
    ? known[0].metrics.filter((metric) => known.every((item) => item.metrics.includes(metric)))
    : [];
  const faults = valid
    ? known[0].faults.filter((fault) => known.every((item) => item.faults.includes(fault)))
    : [];
  const kinds: RuleDefinition['condition']['kind'][] = [];
  if (valid && known.every((item) => item.heartbeatSupported)) kinds.push('no_data');
  if (metrics.length) { kinds.push('numeric'); if (!types.includes('MACHINE_ACTIVITY')) kinds.push('delta'); }
  if (valid && known.every((item) => item.faults.length > 0)) kinds.push('fault');
  if (valid && known.every((item) => item.messageSupported)) kinds.push('message');
  return { metrics, faults, kinds };
}

export function initialCondition(
  kind: RuleDefinition['condition']['kind'],
  metrics: string[]
): RuleDefinition['condition'] {
  switch (kind) {
    case 'no_data':
      return { kind, maxAgeMinutes: 1440 };
    case 'numeric':
      return {
        kind,
        metric: metrics[0] || '',
        operator: 'below',
        threshold: 0,
        recoveryThreshold: null,
        thresholdSource: 'rule',
        ...(metrics.includes('completed_week_hours') ? { comparison: 'strict' as const, recoveryComparison: 'inclusive' as const } : {}),
      };
    case 'delta':
      return { kind, metric: metrics[0] || '', threshold: 20, windowMinutes: 120 };
    case 'fault':
      return { kind, key: '*' };
    case 'message':
      return { kind, contains: '*' };
  }
}

export function newRule(
  capabilities: Capability[],
  scope?: SensorScope,
  sensorType?: string
): RuleDefinition {
  const types = sensorType ? [sensorType] : capabilities.length ? [capabilities[0].sensorType] : [];
  const available = commonCapabilities(capabilities, types);
  const condition = initialCondition(available.kinds[0] || 'no_data', available.metrics);
  return {
    title: '',
    description: '',
    enabled: false,
    mode: 'test',
    scope: { sensorTypes: types, orgId: scope?.orgId || null, sensorId: scope?.sensorId || null },
    condition,
    durationMinutes: 0,
    recoveryMinutes: 0,
    recovery: condition.kind === 'message' ? 'manual' : 'automatic',
    severity: 'warning',
    actions: {
      notifyInApp: true,
      notifyEmail: false,
      notifyOnRecovery: false,
      reminderMinutes: null,
      recipients: 'responsibles',
    },
  };
}

export function editableRule(rule: AlertRule): RuleDefinition {
  return ruleDefinitionSchema.parse(rule);
}

export function validateRule(rule: RuleDefinition, capabilities: Capability[]): string | null {
  const parsed = ruleDefinitionSchema.safeParse(rule);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    if (issue.path[0] === 'title') return 'Skriv et navn til reglen (højst 120 tegn).';
    if (issue.path.includes('reminderMinutes'))
      return 'Påmindelser skal have mindst 15 minutters mellemrum.';
    if (issue.path.includes('contains'))
      return 'Skriv SMS-teksten, eller brug * for alle beskeder.';
    return 'Kontrollér de udfyldte felter. Tidsrum skal være gyldige tal, og mindst én enhedstype skal være valgt.';
  }
  if (rule.scope.sensorId && !rule.scope.orgId)
    return 'En bestemt enhed skal være knyttet til sin organisation.';
  if (rule.scope.sensorTypes.includes('MACHINE_ACTIVITY')) {
    const c = rule.condition;
    if (!rule.scope.sensorId || rule.scope.sensorTypes.length !== 1) return 'Vælg én maskinaktivitetssensor.';
    if (c.kind === 'numeric' && (c.threshold < 0 || (c.metric === 'inactive_days' && c.threshold > 365)))
      return 'Brug en positiv aktivitetsgrænse eller nul; inaktivitet kan højst være 365 dage.';
    if (c.kind === 'numeric' && (c.thresholdSource !== 'rule' || rule.durationMinutes !== 0 || rule.recoveryMinutes !== 0 ||
        (c.metric === 'completed_week_hours' && c.operator !== 'below') || (c.metric === 'inactive_days' && c.operator !== 'above')))
      return 'Brug ugentlige timer under eller inaktive dage over en fast grænse, uden ekstra varighed.';
  }
  const support = commonCapabilities(capabilities, rule.scope.sensorTypes);
  const c = rule.condition;
  if (!support.kinds.includes(c.kind))
    return 'Denne betingelse understøttes ikke af alle de valgte enhedstyper.';
  if ((c.kind === 'numeric' || c.kind === 'delta') && !support.metrics.includes(c.metric))
    return 'Vælg en måling, som alle de valgte enhedstyper understøtter.';
  if (c.kind === 'fault' && c.key !== '*' && !support.faults.includes(c.key))
    return 'Vælg en fejl, som alle de valgte enhedstyper understøtter.';
  if (c.kind === 'message' && rule.recovery !== 'manual')
    return 'SMS-regler skal afsluttes manuelt; stilhed beviser ikke, at fejlen er rettet.';
  if (
    c.kind === 'numeric' &&
    c.thresholdSource === 'rule' &&
    c.recoveryThreshold !== null &&
    ((c.operator === 'below' && c.recoveryThreshold < c.threshold) ||
      (c.operator === 'above' && c.recoveryThreshold > c.threshold))
  ) {
    return 'Grænsen for normal drift skal ligge på den normale side af alarmgrænsen.';
  }
  return null;
}

export function ruleSummary(rule: RuleDefinition): string {
  const c = rule.condition;
  if (c.kind === 'no_data') return `Ingen data i mere end ${c.maxAgeMinutes} minutter.`;
  if (c.kind === 'fault') return faultLabels[c.key] || c.key;
  if (c.kind === 'message')
    return c.contains === '*' ? 'Alle SMS-beskeder.' : `SMS indeholder “${c.contains}”.`;
  if (c.kind === 'delta')
    return `${metricLabels[c.metric] || c.metric}: ændring på mindst ${c.threshold} inden for ${
      c.windowMinutes
    } minutter.`;
  if (c.thresholdSource === 'device')
    return `${metricLabels[c.metric] || c.metric}: bruger enhedens gemte grænser.`;
  const comparison = c.comparison === 'strict'
    ? c.operator === 'below' ? 'under' : 'over'
    : c.operator === 'below' ? 'højst' : 'mindst';
  return `${metricLabels[c.metric] || c.metric}: ${comparison} ${c.threshold}.` +
    (c.roundingDecimals !== undefined ? ` Målingen rundes ned til ${c.roundingDecimals} decimaler.` : '') +
    (c.recoveryDeltaFromOpening !== undefined ? ` Normal drift efter en stigning på over ${c.recoveryDeltaFromOpening} fra første alarmmåling.` : '');
}
